/* eslint-disable max-lines */
import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { Trans, useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';
import { InfoIcon } from '@modernloop/shared/icons';
import { EmployeeId } from '@modernloop/shared/ids';
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

import {
  DynamicAudience,
  EmployeeByIdsQuery,
  InterviewMeetingLocationSettingsInput,
  NotificationPreferenceInput,
  OptionsInput,
  SelfScheduleOptionsSettingInput,
  SelfSchedulePreferences_InterviewMeetingLocationFragment,
  TemplateType,
} from 'src/generated/mloop-graphql';

import useEqualSizeSxProps from 'src/components/utils/useEqualSizeSxProps';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import SmartEmployeeSelect, { SmartEmployeeSelectResult } from 'src/entities/Select/SmartEmployeeSelect';
import { DynamicAudienceContext } from 'src/entities/Select/SmartEmployeeSelect/types';
import TemplateSelect from 'src/entities/Template/TemplateSelect';

import CalendarSelect from 'src/views-new/ScheduleFlow/Steps/Communications/CalendarCard/CalendarSelect';

import { KeywordTooltip } from './KeywordTooltip';
import RequiredAdvanceNotice from './RequiredAdvanceNotice';
import SelfScheduleInterviewMeetingLocation from './SelfScheduleInterviewMeetingLocation';

export interface SelfScheduleSelectedTemplates {
  candidateEmailTemplateId?: string;
  candidateEventTemplateId?: string;
  interviewerEventTemplateId?: string;
  requestEmailTemplateId?: string;
}

type Fragments = {
  interviewMeetingLocation: SelfSchedulePreferences_InterviewMeetingLocationFragment | undefined;
  candidateEventAdditionalAttendees: NotificationPreferenceInput | null;
  interviewerEventAdditionalAttendees: NotificationPreferenceInput | null;
};

type AdditionalAttendees = {
  employeeIds?: EmployeeId[];
  externalEmailAddresses?: string[];
  dynamicAudiences?: DynamicAudience[];
};

type Props = {
  title?: string;
  alertJsx?: JSX.Element | boolean;
  onOptionsChanged: (value: SelfScheduleOptionsSettingInput) => void;
  options: SelfScheduleOptionsSettingInput;
  templates: SelfScheduleSelectedTemplates;
  defaultTemplates: SelfScheduleSelectedTemplates;
  isPersonalizedEmailChecked: boolean;
  onNoteChanged: (value: string) => void;
  onTemplatesChanged: (value: SelfScheduleSelectedTemplates) => void;
  missingEmployeesData: EmployeeByIdsQuery | undefined;
  handlePersonalizedEmailCheckboxChange: (checked: boolean) => void;
  onInterviewMeetingLocationChanged: (interviewMeetingLocation: InterviewMeetingLocationSettingsInput) => void;
  onCandidateEventAdditionalAttendeesChanged?: (attendees: NotificationPreferenceInput) => void;
  onInterviewerEventAdditionalAttendeesChanged?: (attendees: NotificationPreferenceInput) => void;
};

type AdditionalRecipientsSectionProps = {
  title: string;
  placeholder: string;
  tooltipContent: React.ReactNode;
  context: DynamicAudienceContext;
  additionalAttendees: AdditionalAttendees | null;
  filterDynamicAudiences: DynamicAudience[];
  equalSizeChildSx: SxProps<Theme>;
  onChange: (result: SmartEmployeeSelectResult) => void;
  dataTestId: string;
};

/**
 * Renders a self-schedule additional recipients section with tooltip and selector.
 */
const AdditionalRecipientsSection = ({
  title,
  placeholder,
  tooltipContent,
  context,
  additionalAttendees,
  filterDynamicAudiences,
  equalSizeChildSx,
  onChange,
  dataTestId,
}: AdditionalRecipientsSectionProps): JSX.Element => (
  <Stack
    alignItems="center"
    direction={{ xs: 'column', sm: 'row' }}
    justifyContent="space-between"
    spacing={1}
    sx={equalSizeChildSx}
    data-testid={dataTestId}
  >
    <Box data-testid={`${dataTestId}-label`}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Typography component="span">{title}</Typography>
        <Tooltip title={tooltipContent}>
          <Box component="span" sx={{ display: 'inline-flex', cursor: 'pointer' }}>
            <InfoIcon sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
          </Box>
        </Tooltip>
      </Box>
    </Box>
    <Box data-testid={`${dataTestId}-select`}>
      <SmartEmployeeSelect
        label=""
        placeholder={placeholder}
        context={context}
        selectedEmployeeIds={additionalAttendees?.employeeIds || []}
        selectedDynamicAudiences={additionalAttendees?.dynamicAudiences || []}
        selectedFreeformEmails={additionalAttendees?.externalEmailAddresses || []}
        filterDynamicAudiences={filterDynamicAudiences}
        onChange={onChange}
      />
    </Box>
  </Stack>
);

/**
 * Self schedule preferences for scheduling behavior, templates, and notifications.
 */
const SelfSchedulePreferences: FCWithFragments<Fragments, Props> = ({
  title,
  alertJsx,
  onOptionsChanged,
  options,
  templates,
  defaultTemplates,
  onNoteChanged,
  onTemplatesChanged,
  missingEmployeesData,
  isPersonalizedEmailChecked,
  handlePersonalizedEmailCheckboxChange,
  interviewMeetingLocation,
  onInterviewMeetingLocationChanged,
  candidateEventAdditionalAttendees,
  interviewerEventAdditionalAttendees,
  onCandidateEventAdditionalAttendeesChanged,
  onInterviewerEventAdditionalAttendeesChanged,
}) => {
  const { t } = useTranslation();
  const sxProps = useEqualSizeSxProps();
  const isAdditionalAttendeesEnabled = useFlag('org_add_additional_event_attendees');
  const freeTimesTitle = t('hiring-dashboard-schedule:schedule.selfSchedulePreferences.freeTimes.title');
  const freeTimesDescription = t('hiring-dashboard-schedule:schedule.selfSchedulePreferences.freeTimes.description');
  const scheduleOverFreeTimeLabel = t(
    'hiring-dashboard-schedule:schedule.selfSchedulePreferences.scheduleOverFreeTime'
  );
  const recruitingBlocksTitle = t('hiring-dashboard-schedule:schedule.selfSchedulePreferences.recruitingBlocks.title');
  const recruitingBlocksDescription = t(
    'hiring-dashboard-schedule:schedule.selfSchedulePreferences.recruitingBlocks.description'
  );
  const scheduleOverRecruitingBlockKeywordsLabel = t(
    'hiring-dashboard-schedule:schedule.selfSchedulePreferences.scheduleOverRecruitingBlockKeywords'
  );
  const availableTitle = t('hiring-dashboard-schedule:schedule.selfSchedulePreferences.available.title');
  const availableDescription = t('hiring-dashboard-schedule:schedule.selfSchedulePreferences.available.description');
  const scheduleOverAvailableKeywordsLabel = t(
    'hiring-dashboard-schedule:schedule.selfSchedulePreferences.scheduleOverAvailableKeywords'
  );
  const loadLimitsTitle = t('hiring-dashboard-schedule:schedule.selfSchedulePreferences.loadLimits.title');
  const loadLimitsDescription = t('hiring-dashboard-schedule:schedule.selfSchedulePreferences.loadLimits.description');
  const ignoreInterviewerLoadLimitsLabel = t(
    'hiring-dashboard-schedule:schedule.selfSchedulePreferences.ignoreInterviewerLoadLimits'
  );
  const candidateNotePlaceholder = t(
    'hiring-dashboard-schedule:schedule.selfSchedulePreferences.candidateNote.placeholder'
  );
  const candidateEventAndEmailDetailsHeading = t(
    'hiring-dashboard-schedule:schedule.selfSchedulePreferences.candidateEventAndEmailDetails'
  );
  const bookOnCalendarLabel = t('hiring-dashboard-schedule:schedule.selfSchedulePreferences.bookOnCalendar');
  const eventDetailsLabel = t('hiring-dashboard-schedule:schedule.selfSchedulePreferences.eventDetails');
  const sendPersonalizedEmailLabel = t(
    'hiring-dashboard-schedule:schedule.selfSchedulePreferences.sendPersonalizedEmail'
  );
  const personalizedEmailCaption = t(
    'hiring-dashboard-schedule:schedule.selfSchedulePreferences.personalizedEmailCaption'
  );
  const candidateAdditionalRecipientsTitle = t(
    'hiring-dashboard-schedule:schedule.selfSchedulePreferences.additionalRecipients.candidateEvent'
  );
  const internalAdditionalRecipientsTitle = t(
    'hiring-dashboard-schedule:schedule.selfSchedulePreferences.additionalRecipients.internalEvent'
  );
  const additionalRecipientsPlaceholder = t(
    'hiring-dashboard-schedule:schedule.selfSchedulePreferences.additionalRecipients.addEmails'
  );
  const interviewerEventDetailsHeading = t(
    'hiring-dashboard-schedule:schedule.selfSchedulePreferences.interviewerEventDetails'
  );
  const additionalAttendeesTooltip = (
    <Trans
      i18nKey="hiring-dashboard-schedule:schedule.selfSchedulePreferences.additionalRecipients.tooltip"
      components={{ br: <br /> }}
    />
  );

  const additionalAttendeesContext = {
    job: true,
    application: true,
    schedule: true,
    task: true,
    enableOptionsWithMissingData: true,
  } as const;

  const filterDynamicAudiences = [
    DynamicAudience.DebriefAttendees,
    DynamicAudience.InterviewersOnSchedule,
    DynamicAudience.CandidatesPreviousInterviewers,
  ];

  const candidateAdditionalAttendees = useMemo(() => {
    if (!candidateEventAdditionalAttendees) {
      return null;
    }
    return {
      employeeIds: EmployeeId.parseArray(candidateEventAdditionalAttendees.employeeIds || []),
      dynamicAudiences: candidateEventAdditionalAttendees.dynamicAudiences || [],
      externalEmailAddresses: candidateEventAdditionalAttendees.externalEmailAddresses || [],
    };
  }, [candidateEventAdditionalAttendees]);

  const interviewerAdditionalAttendees = useMemo(() => {
    if (!interviewerEventAdditionalAttendees) {
      return null;
    }
    return {
      employeeIds: EmployeeId.parseArray(interviewerEventAdditionalAttendees.employeeIds || []),
      dynamicAudiences: interviewerEventAdditionalAttendees.dynamicAudiences || [],
      externalEmailAddresses: interviewerEventAdditionalAttendees.externalEmailAddresses || [],
    };
  }, [interviewerEventAdditionalAttendees]);

  return (
    <Stack
      direction="column"
      sx={{
        margin: 0,
      }}
      spacing={2}
    >
      <Stack direction="column" spacing={1}>
        {title && <Typography variant="subtitle1">{title}</Typography>}

        <KeywordTooltip title={freeTimesTitle} description={freeTimesDescription}>
          <FormControlLabel
            sx={{
              justifyContent: 'space-between',
            }}
            control={
              <Switch
                checked={!!options.canScheduleOverFreeTime}
                onChange={() =>
                  onOptionsChanged({
                    ...options,
                    canScheduleOverFreeTime: !options.canScheduleOverFreeTime,
                  })
                }
              />
            }
            labelPlacement="start"
            label={scheduleOverFreeTimeLabel}
          />
        </KeywordTooltip>

        <KeywordTooltip title={recruitingBlocksTitle} description={recruitingBlocksDescription}>
          <FormControlLabel
            sx={{
              justifyContent: 'space-between',
            }}
            control={
              <Switch
                checked={!!options.canScheduleOverRecruitingKeywords}
                onChange={() =>
                  onOptionsChanged({
                    ...options,
                    canScheduleOverRecruitingKeywords: !options.canScheduleOverRecruitingKeywords,
                  })
                }
              />
            }
            labelPlacement="start"
            label={scheduleOverRecruitingBlockKeywordsLabel}
          />
        </KeywordTooltip>

        <KeywordTooltip title={availableTitle} description={availableDescription}>
          <FormControlLabel
            sx={{
              justifyContent: 'space-between',
            }}
            control={
              <Switch
                checked={!!options.canScheduleOverAvailableKeywords}
                onChange={() =>
                  onOptionsChanged({
                    ...options,
                    canScheduleOverAvailableKeywords: !options.canScheduleOverAvailableKeywords,
                  })
                }
              />
            }
            labelPlacement="start"
            label={scheduleOverAvailableKeywordsLabel}
          />
        </KeywordTooltip>

        {(options.canScheduleOverAvailableKeywords ||
          options.canScheduleOverRecruitingKeywords ||
          options.canScheduleOverFreeTime) && (
          <KeywordTooltip title={loadLimitsTitle} description={loadLimitsDescription}>
            <FormControlLabel
              sx={{
                justifyContent: 'space-between',
              }}
              control={
                <Switch
                  checked={!options.shouldRespectLoadLimit}
                  onChange={() =>
                    onOptionsChanged({
                      ...options,
                      shouldRespectLoadLimit: !options.shouldRespectLoadLimit,
                    })
                  }
                />
              }
              labelPlacement="start"
              label={ignoreInterviewerLoadLimitsLabel}
            />
          </KeywordTooltip>
        )}

        <RequiredAdvanceNotice
          value={options.advanceNoticeInHours}
          onChange={(value) => onOptionsChanged({ ...options, advanceNoticeInHours: value || 0 })}
        />

        {alertJsx}
      </Stack>

      <Divider />

      <SelfScheduleInterviewMeetingLocation
        interviewMeetingLocation={interviewMeetingLocation}
        missingEmployeesData={missingEmployeesData}
        onChange={onInterviewMeetingLocationChanged}
      />

      <Divider />

      <>
        <Stack spacing={1}>
          <Typography color="foreground">
            <Trans
              i18nKey="hiring-dashboard-schedule:schedule.selfSchedulePreferences.candidateNote.label"
              components={{ strong: <strong style={{ fontWeight: 600 }} /> }}
            />
          </Typography>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            minRows={4}
            defaultValue={
              (options as SelfScheduleOptionsSettingInput)?.candidateNote || (options as OptionsInput)?.note || ''
            }
            placeholder={candidateNotePlaceholder}
            onBlur={(candidateNote) => onNoteChanged(candidateNote.target.value)}
          />
        </Stack>
        <Divider />
      </>

      <Stack direction="column" spacing={2}>
        <Typography fontWeight={600}>{candidateEventAndEmailDetailsHeading}</Typography>
        <Alert severity="info">
          <Typography color="text.secondary" variant="caption">
            <Trans
              i18nKey="hiring-dashboard-schedule:schedule.selfSchedulePreferences.candidateEventAndEmailDetails.alert"
              components={{
                learnMoreLink: (
                  <Link
                    color="info"
                    variant="caption"
                    href={HelpCenterUrl.SELF_SCHEDULE_EVENT_DESC_CONFIRMATION_EMAILS}
                    target="_blank"
                  />
                ),
              }}
            />
          </Typography>
        </Alert>
        <Stack
          alignItems="center"
          direction={{ xs: 'column', sm: 'row' }}
          sx={sxProps.equalSizeChild}
          spacing={1}
          justifyContent="space-between"
        >
          <Box>
            <Typography>{bookOnCalendarLabel}</Typography>
          </Box>
          <Box>
            <CalendarSelect
              dataTestId="self-schedule-options-candidate-calendar-google-select"
              error={!options.candidateCalendarId}
              calendarId={options.candidateCalendarId}
              onChange={(candidateCalendarId) => {
                // Dont add a return check for falsy value
                onOptionsChanged({ ...options, candidateCalendarId: candidateCalendarId || '' });
              }}
              resetUnknownCalendarsOnLoad={false}
            />
          </Box>
        </Stack>
        <Stack
          alignItems="center"
          direction={{ xs: 'column', sm: 'row' }}
          sx={sxProps.equalSizeChild}
          spacing={1}
          justifyContent="space-between"
        >
          <Typography>{eventDetailsLabel}</Typography>
          <Box>
            <TemplateSelect
              useSelectButton
              showPreviews
              hideTitle
              labelColor="foreground"
              type={TemplateType.CandidateInvite}
              selectedTemplateId={templates.candidateEventTemplateId}
              onTemplateSelect={(template) =>
                onTemplatesChanged({
                  ...templates,
                  candidateEventTemplateId: template.id,
                })
              }
            />
          </Box>
        </Stack>

        {/*
        Custom Interview Schedule tokens are not available.
        <Stack direction="row" justifyContent="space-between">
          <Box sx={{ width: '55%' }}>
            <Label>Event details</Label>
          </Box>
          <Box sx={{ width: '40%' }}>
            <TemplateSelect
              useSelectButton
              showErrorOnEmpty
              showPreviews
              hideTitle
              labelColor="foreground"
              selectedTemplateId={templates.candidateEventTemplateId}
              types={[TemplateType.CandidateInvite]}
              onTemplateSelect={(template) =>
                onTemplatesChanged({
                  ...templates,
                  candidateEventTemplateId: template.id,
                })
              }
            />
          </Box>
        </Stack> */}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          sx={sxProps.equalSizeChild}
          spacing={1}
          justifyContent="space-between"
        >
          <Box sx={{ marginLeft: '-8px' }}>
            <FormControlLabel
              control={<Checkbox checked={isPersonalizedEmailChecked} />}
              label={
                <Box>
                  <Typography>{sendPersonalizedEmailLabel}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {personalizedEmailCaption}
                  </Typography>
                </Box>
              }
              onChange={(event: React.SyntheticEvent<HTMLInputElement>) => {
                handlePersonalizedEmailCheckboxChange(event.currentTarget.checked);

                if (event.currentTarget.checked) {
                  onTemplatesChanged({
                    ...templates,
                    candidateEmailTemplateId: defaultTemplates.candidateEmailTemplateId,
                  });
                } else {
                  onTemplatesChanged({
                    ...templates,
                    candidateEmailTemplateId: undefined,
                  });
                }
              }}
            />
          </Box>
          {isPersonalizedEmailChecked && (
            <Box>
              <TemplateSelect
                useSelectButton
                showErrorOnEmpty
                showPreviews
                hideTitle
                labelColor="foreground"
                type={TemplateType.CandidateConfirmation}
                selectedTemplateId={templates.candidateEmailTemplateId}
                onTemplateSelect={(template) =>
                  onTemplatesChanged({
                    ...templates,
                    candidateEmailTemplateId: template.id,
                  })
                }
              />
            </Box>
          )}
        </Stack>
        {isAdditionalAttendeesEnabled && onCandidateEventAdditionalAttendeesChanged && (
          <AdditionalRecipientsSection
            title={candidateAdditionalRecipientsTitle}
            placeholder={additionalRecipientsPlaceholder}
            tooltipContent={additionalAttendeesTooltip}
            context={additionalAttendeesContext}
            additionalAttendees={candidateAdditionalAttendees}
            filterDynamicAudiences={filterDynamicAudiences}
            equalSizeChildSx={sxProps.equalSizeChild}
            dataTestId="self-schedule-preferences-candidate-additional-recipients-default"
            onChange={({ selectedEmployeeIds, dynamicAudiences, selectedEmailAddresses }) => {
              onCandidateEventAdditionalAttendeesChanged({
                dynamicAudiences,
                externalEmailAddresses: selectedEmailAddresses,
                employeeIds: selectedEmployeeIds,
              });
            }}
          />
        )}
      </Stack>

      <Divider />

      <Stack direction="column" spacing={2}>
        <Typography fontWeight={600}>{interviewerEventDetailsHeading}</Typography>

        <Stack
          alignItems="center"
          direction={{ xs: 'column', sm: 'row' }}
          sx={sxProps.equalSizeChild}
          spacing={1}
          justifyContent="space-between"
        >
          <Box>
            <Typography>{bookOnCalendarLabel}</Typography>
          </Box>
          <Box>
            <CalendarSelect
              dataTestId="self-schedule-options-interviewer-calendar-google-select"
              error={!options.interviewerCalendarId}
              calendarId={options.interviewerCalendarId}
              onChange={(interviewerCalendarId) => {
                // Dont add a return check for falsy value
                onOptionsChanged({
                  ...options,
                  interviewerCalendarId: interviewerCalendarId || '',
                });
              }}
              resetUnknownCalendarsOnLoad={false}
            />
          </Box>
        </Stack>

        <Stack
          alignItems="center"
          direction={{ xs: 'column', sm: 'row' }}
          sx={sxProps.equalSizeChild}
          spacing={1}
          justifyContent="space-between"
        >
          <Typography>{eventDetailsLabel}</Typography>
          <Box>
            <TemplateSelect
              useSelectButton
              showErrorOnEmpty
              showPreviews
              hideTitle
              labelColor="foreground"
              type={TemplateType.InterviewerInvite}
              selectedTemplateId={templates.interviewerEventTemplateId}
              onTemplateSelect={(template) =>
                onTemplatesChanged({
                  ...templates,
                  interviewerEventTemplateId: template.id,
                })
              }
            />
          </Box>
        </Stack>
        {isAdditionalAttendeesEnabled && onInterviewerEventAdditionalAttendeesChanged && (
          <AdditionalRecipientsSection
            title={internalAdditionalRecipientsTitle}
            placeholder={additionalRecipientsPlaceholder}
            tooltipContent={additionalAttendeesTooltip}
            context={additionalAttendeesContext}
            additionalAttendees={interviewerAdditionalAttendees}
            filterDynamicAudiences={filterDynamicAudiences}
            equalSizeChildSx={sxProps.equalSizeChild}
            dataTestId="self-schedule-preferences-interviewer-additional-recipients-default"
            onChange={({ selectedEmployeeIds, dynamicAudiences, selectedEmailAddresses }) => {
              onInterviewerEventAdditionalAttendeesChanged({
                dynamicAudiences,
                externalEmailAddresses: selectedEmailAddresses,
                employeeIds: selectedEmployeeIds,
              });
            }}
          />
        )}
      </Stack>
    </Stack>
  );
};

SelfSchedulePreferences.fragments = {
  interviewMeetingLocation: gql`
    ${SelfScheduleInterviewMeetingLocation.fragments.interviewMeetingLocation}
    fragment SelfSchedulePreferences_interviewMeetingLocation on InterviewMeetingLocationSettingPref {
      ...SelfScheduleInterviewMeetingLocation_interviewMeetingLocation
    }
  `,
  candidateEventAdditionalAttendees: gql`
    fragment SelfSchedulePreferences_candidateEventAdditionalAttendees on CandidateEventAdditionalAttendeesPref {
      candidateEventAdditionalAttendees {
        employeeIds
        dynamicAudiences
        externalEmailAddresses
      }
    }
  `,
  interviewerEventAdditionalAttendees: gql`
    fragment SelfSchedulePreferences_interviewerEventAdditionalAttendees on InterviewerEventAdditionalAttendeesPref {
      interviewerEventAdditionalAttendees {
        employeeIds
        dynamicAudiences
        externalEmailAddresses
      }
    }
  `,
};

export default SelfSchedulePreferences;
